import { LOCALE_KEYS } from "@foodlista/shared/i18n/constants"
import { z } from "zod"
import { defineOperationContext } from "../define"
import type { MenuTranslateInput } from "../schemas/menu-translate"

export const menuTranslateContextSchema = z.object({
	flowId: z.string().uuid(),
	menuId: z.string().uuid().optional(),
	/** Route key of the enriched menu; absent for the product-catalog flow. */
	menuSlug: z.string().min(1).max(200).optional(),
	sourceLocale: z.enum(LOCALE_KEYS),
	productCount: z.number().int().nonnegative(),
})

export type MenuTranslateContext = z.infer<typeof menuTranslateContextSchema>

/**
 * Items live either directly on a section or on one of its subsections. The
 * count is descriptive only — it records how many entities the run was asked
 * to translate, so a persisted envelope can be read without its input.
 */
function countMenuItems(menu: MenuTranslateInput["menu"]): number {
	let total = 0

	for (const section of menu.sections) {
		total += section.items.length

		for (const subsection of section.subsections ?? []) {
			total += subsection.items.length
		}
	}

	return total
}

export const menuTranslateContextSpec = defineOperationContext<
	MenuTranslateInput,
	MenuTranslateContext
>({
	version: 1,
	schema: menuTranslateContextSchema,
	fromInput: (input) => ({
		flowId: input.flowId,
		menuId: input.menuId,
		menuSlug: input.menuSlug,
		sourceLocale: input.menu.locale,
		productCount: countMenuItems(input.menu),
	}),
})
